import {
  DatabaseFile,
  DatabaseFileImageMetadata,
  DatabaseFileImageVariation,
  DatabaseFileMetadata,
} from '../../src/app/shared/models/entity';
import {
  auditFields,
  createMany,
  cycle,
  FixtureBuilder,
  FixtureFactory,
  fixtureId,
} from './fixture.utils';

// -----------------------------------------------------------------------------------------------------
// @ TYPES
// -----------------------------------------------------------------------------------------------------

/**
 * A file that physically exists in the Angular `public/` folder.
 *
 * The registry is the single source of truth for the mock files: the fixture derives the url, the
 * extension, the metadata and the variations from it, so adding an image means adding one entry
 * here — no fixture, seed or store needs to know the folder exists.
 */
export interface MockAsset {
  /** The path inside `public/`, e.g. 'property/1.jpg'. Never shown to the user. */
  path: string;
  /** The display name, i.e. what the file was uploaded as. This is what lands on `DatabaseFile.name`. */
  displayName: string;
  /** The MIME type of the asset. */
  contentType: string;
  /** The size in bytes. Approximate is fine — it only drives the UI. */
  size: number;
  /** The tags the backend would have attached on upload. */
  tags?: string[];
}

// -----------------------------------------------------------------------------------------------------
// @ ASSET REGISTRY
// -----------------------------------------------------------------------------------------------------

/**
 * Where the mock assets are served from. Angular serves `public/` at the app root, so an asset at
 * `public/property/1.jpg` is reachable at `/property/1.jpg`.
 *
 * This is the seam that goes away with the mock: in production the url comes from the storage
 * bucket, and nothing else about the file changes.
 */
export const MOCK_ASSET_BASE_URL: string = '/';

/** The bucket name the mock reports. Real uploads will carry the actual bucket instead. */
export const MOCK_BUCKET_NAME: string = 'local-public';

export const FIXTURE_TERMS_VERSION: string = '1.0';

export const FIXTURE_DATA_PROTECTION_VERSION: string = '1.0';

/** The `fileType` discriminator — mirrors what the backend stores alongside the MIME type. */
export const FILE_TYPE_DOCUMENT: string = 'Document';

export const FILE_TYPE_IMAGE: string = 'Image';

/** The property photos in `public/property/`. Add a file, add a line — that's the whole change. */
export const PROPERTY_IMAGE_ASSETS: readonly MockAsset[] = [
  {
    path: 'property/1.jpg',
    displayName: 'Fassade.jpg',
    contentType: 'image/jpeg',
    size: 1_204_887,
    tags: ['foto', 'aussenansicht'],
  },
  {
    path: 'property/2.jpg',
    displayName: 'Innenhof.jpg',
    contentType: 'image/jpeg',
    size: 986_331,
    tags: ['foto', 'innenhof'],
  },
  {
    path: 'property/3.jpg',
    displayName: 'Dach.jpg',
    contentType: 'image/jpeg',
    size: 1_102_443,
    tags: ['foto', 'dach'],
  },
  {
    path: 'property/4.jpg',
    displayName: 'Front.jpg',
    contentType: 'image/jpeg',
    size: 1_331_331,
    tags: ['foto', 'front'],
  },
  {
    path: 'property/5.jpg',
    displayName: 'Fassade.jpg',
    contentType: 'image/jpeg',
    size: 986_331,
    tags: ['foto', 'aussenansicht'],
  },
];

/**
 * The documents in `public/document/`.
 *
 * These paths are a proposal — point them at whatever actually sits in `public/`. Any entry whose
 * file doesn't exist still renders fine everywhere in the UI; it only 404s if someone clicks
 * download, which is a fair trade for not committing five real PDFs to the repo.
 */
export const DOCUMENT_ASSETS: readonly MockAsset[] = [
  {
    path: 'document/1.pdf',
    displayName: 'Teilungserklärung.pdf',
    contentType: 'application/pdf',
    size: 2_411_233,
    tags: ['teilungserklaerung', 'grundlagen'],
  },
  {
    path: 'document/2.pdf',
    displayName: 'Wirtschaftsplan_2026.pdf',
    contentType: 'application/pdf',
    size: 512_004,
    tags: ['wirtschaftsplan', '2026'],
  },
  {
    path: 'document/3.pdf',
    displayName: 'Protokoll_Eigentuemerversammlung.pdf',
    contentType: 'application/pdf',
    size: 187_540,
    tags: ['protokoll', 'etv'],
  },
  {
    path: 'document/4.pdf',
    displayName: 'Jahresabrechnung_2025.pdf',
    contentType: 'application/pdf',
    size: 964_220,
    tags: ['abrechnung', '2025'],
  },
];

const ALLOWED_USAGES: readonly string[] = ['Preview', 'Download'];

// -----------------------------------------------------------------------------------------------------
// @ ASSET HELPERS
// -----------------------------------------------------------------------------------------------------

/**
 * Builds the url an asset is served under.
 *
 * @param path - The path inside `public/`, e.g. 'property/1.jpg'.
 * @returns {string} The url, e.g. '/property/1.jpg'.
 */
export function toAssetUrl(path: string): string {
  return `${MOCK_ASSET_BASE_URL}${path.replace(/^\/+/, '')}`;
}

/**
 * Derives the extension from a display name.
 *
 * @param displayName - The display name, including the extension.
 * @returns {string} The extension, without the dot.
 */
export function toExtension(displayName: string): string {
  return displayName.split('.').pop()?.toLowerCase() ?? '';
}

/**
 * Whether an asset is an image, and therefore carries image metadata and variations.
 *
 * @param asset - The asset to test.
 * @returns {boolean} True when the asset is an image.
 */
export function isImageAsset(asset: MockAsset): boolean {
  return asset.contentType.startsWith('image/');
}

// -----------------------------------------------------------------------------------------------------
// @ FACTORY
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a DatabaseFile from an asset in the public folder. This is the primitive the other
 * factories are built on — reach for it directly when a screen needs a specific asset.
 *
 * @param asset - The asset backing the file.
 * @param overrides - The fields to override.
 * @param index - The zero-based index, driving the deterministic id.
 * @returns {DatabaseFile} A fresh DatabaseFile instance.
 */
export function createFileFromAsset(
  asset: MockAsset,
  overrides: Partial<DatabaseFile> = {},
  index: number = 0,
): DatabaseFile {
  const isImage: boolean = isImageAsset(asset);
  const url: string = toAssetUrl(asset.path);

  return new DatabaseFile({
    id: fixtureId(isImage ? 'file-image' : 'file', index),
    ...auditFields(index),
    bucketName: MOCK_BUCKET_NAME,
    name: asset.displayName,
    size: asset.size,
    contentType: asset.contentType,
    fileType: isImage ? FILE_TYPE_IMAGE : FILE_TYPE_DOCUMENT,
    url,
    extension: toExtension(asset.displayName),
    termsVersion: FIXTURE_TERMS_VERSION,
    dataProtectionVersion: FIXTURE_DATA_PROTECTION_VERSION,
    metadata: isImage
      ? createImageMetadata(asset.displayName)
      : createFileMetadata(asset.displayName),
    tags: [...(asset.tags ?? [])],
    allowedUsages: [...ALLOWED_USAGES],
    variations: isImage ? createImageVariations(asset) : {},
    ...overrides,
  });
}

/**
 * Builds a document DatabaseFile, rotating through the document assets.
 *
 * @param overrides - The fields to override.
 * @param index - The zero-based index, driving the deterministic id and the asset rotation.
 * @returns {DatabaseFile} A fresh DatabaseFile instance.
 */
export const createFile: FixtureFactory<DatabaseFile> = (
  overrides: Partial<DatabaseFile> = {},
  index: number = 0,
): DatabaseFile => {
  return createFileFromAsset(cycle(DOCUMENT_ASSETS, index), overrides, index);
};

/**
 * Builds an image DatabaseFile, rotating through the property images in `public/property/`.
 *
 * @param overrides - The fields to override.
 * @param index - The zero-based index, driving the deterministic id and the asset rotation.
 * @returns {DatabaseFile} A fresh image DatabaseFile instance.
 */
export const createImageFile: FixtureFactory<DatabaseFile> = (
  overrides: Partial<DatabaseFile> = {},
  index: number = 0,
): DatabaseFile => {
  return createFileFromAsset(cycle(PROPERTY_IMAGE_ASSETS, index), overrides, index);
};

/**
 * Builds a list of document DatabaseFiles.
 *
 * @param count - How many files to build.
 * @param overrides - Static overrides, or a callback returning overrides per index.
 * @returns {DatabaseFile[]} The built files.
 */
export function createFiles(
  count: number,
  overrides?: Partial<DatabaseFile> | ((index: number) => Partial<DatabaseFile>),
): DatabaseFile[] {
  return createMany(createFile, count, overrides);
}

/**
 * Builds a list of image DatabaseFiles.
 *
 * @param count - How many images to build.
 * @param overrides - Static overrides, or a callback returning overrides per index.
 * @returns {DatabaseFile[]} The built image files.
 */
export function createImageFiles(
  count: number,
  overrides?: Partial<DatabaseFile> | ((index: number) => Partial<DatabaseFile>),
): DatabaseFile[] {
  return createMany(createImageFile, count, overrides);
}

// -----------------------------------------------------------------------------------------------------
// @ METADATA & VARIATIONS
// -----------------------------------------------------------------------------------------------------

/**
 * Builds the metadata bag of a document file.
 *
 * @param originalFileName - The name the file was uploaded under.
 * @returns {Record<string, any>} The metadata bag.
 */
export function createFileMetadata(originalFileName: string): Record<string, any> {
  const metadata: DatabaseFileMetadata = new DatabaseFileMetadata();

  metadata.originalFileName = originalFileName;

  return { ...metadata };
}

/**
 * Builds the metadata bag of an image file.
 *
 * @param originalFileName - The name the file was uploaded under.
 * @returns {Record<string, any>} The metadata bag.
 */
export function createImageMetadata(originalFileName: string): Record<string, any> {
  const metadata: DatabaseFileImageMetadata = new DatabaseFileImageMetadata();

  metadata.originalFileName = originalFileName;

  return { ...metadata };
}

/**
 * Builds the variation bag of an image: original, thumbnail and medium.
 *
 * There is only one file on disk, so every variation points at the same asset — the sizes differ so
 * a UI that picks the smallest rendition still behaves realistically. Once real renditions exist
 * (`property/1-thumb.jpg`), give each variation its own path here and nothing else changes.
 *
 * @param asset - The asset backing the image.
 * @returns {Record<string, any>} The variation bag, keyed by variation name.
 */
export function createImageVariations(asset: MockAsset): Record<string, any> {
  return {
    original: createImageVariation('original', asset, asset.size, true),
    thumbnail: createImageVariation('thumbnail', asset, Math.round(asset.size * 0.05), false),
    medium: createImageVariation('medium', asset, Math.round(asset.size * 0.35), false),
  };
}

// -----------------------------------------------------------------------------------------------------
// @ BASE FIXTURES
// -----------------------------------------------------------------------------------------------------

export const DEFAULT_FILE: DatabaseFile = createFile();

// -----------------------------------------------------------------------------------------------------
// @ SCENARIO FIXTURES
// -----------------------------------------------------------------------------------------------------

/** The Teilungserklärung — the document every WEG has. */
export const FILE_TEILUNGSERKLAERUNG: DatabaseFile = createFile({}, 0);

/** A real image from `public/property/1.jpg` — usable as a `Property.thumbnail`. */
export const FILE_IMAGE: DatabaseFile = createImageFile();

/** A large upload — for size-limit and progress paths. */
export const FILE_LARGE: DatabaseFile = createFile({ size: 48_000_000 }, 1);

/** A file only the manager may preview — for permission paths. */
export const FILE_RESTRICTED: DatabaseFile = createFile({ allowedUsages: ['Preview'] }, 2);

/** A freshly uploaded file — no tags, no variations, nothing processed yet. */
export const FILE_UNPROCESSED: DatabaseFile = createFile(
  { tags: [], allowedUsages: [], variations: {} },
  3,
);

/** A file whose asset does not exist on disk — for broken-image and download-failure paths. */
export const FILE_MISSING_ASSET: DatabaseFile = createFileFromAsset(
  {
    path: 'property/does-not-exist.jpg',
    displayName: 'Kaputt.jpg',
    contentType: 'image/jpeg',
    size: 12_345,
  },
  {},
  99,
);

// -----------------------------------------------------------------------------------------------------
// @ BUILDER
// -----------------------------------------------------------------------------------------------------

export class FileBuilder extends FixtureBuilder<DatabaseFile> {
  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(index: number = 0) {
    super(createFile({}, index));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Backs the file with a specific asset from the public folder — url, name, extension, content
   * type, metadata and variations are all re-derived from it.
   *
   * @param asset - The asset to use.
   * @returns {this} The builder, for chaining.
   */
  fromAsset(asset: MockAsset): this {
    return this.with(createFileFromAsset(asset, {}, 0));
  }

  /**
   * Backs the file with one of the property images in `public/property/`.
   *
   * @param index - The index into the image registry. Wraps around.
   * @returns {this} The builder, for chaining.
   */
  asImage(index: number = 0): this {
    return this.fromAsset(cycle(PROPERTY_IMAGE_ASSETS, index));
  }

  /**
   * Renames the file without touching the asset it points at — the display name and the storage
   * location are independent, which is exactly what the real system does.
   *
   * @param displayName - The new display name, including the extension.
   * @returns {this} The builder, for chaining.
   */
  named(displayName: string): this {
    return this.with({
      name: displayName,
      extension: toExtension(displayName),
      metadata: { ...this.draft.metadata, originalFileName: displayName },
    });
  }

  /**
   * Sets the size of the file, in bytes.
   *
   * @param size - The size in bytes.
   * @returns {this} The builder, for chaining.
   */
  withSize(size: number): this {
    return this.with({ size });
  }

  /**
   * Sets the tags of the file.
   *
   * @param tags - The tags to apply.
   * @returns {this} The builder, for chaining.
   */
  withTags(...tags: string[]): this {
    return this.with({ tags });
  }

  /**
   * Sets which usages are allowed on the file — the lever for permission paths.
   *
   * @param allowedUsages - The allowed usages, e.g. 'Preview', 'Download'.
   * @returns {this} The builder, for chaining.
   */
  withAllowedUsages(...allowedUsages: string[]): this {
    return this.with({ allowedUsages });
  }

  /**
   * Merges variations into the file — pass `{}` to model a file that hasn't been processed yet.
   *
   * @param variations - The variation bag, keyed by variation name.
   * @returns {this} The builder, for chaining.
   */
  withVariations(variations: Record<string, any>): this {
    return this.with({ variations: { ...this.draft.variations, ...variations } });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ OVERRIDE METHODS
  // -----------------------------------------------------------------------------------------------------

  override build(): DatabaseFile {
    return new DatabaseFile(this.draft);
  }
}

// -----------------------------------------------------------------------------------------------------
// @ PRIVATE
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a single image variation, pointing at the asset it was derived from.
 *
 * @param variation - The variation name, e.g. 'thumbnail'.
 * @param asset - The asset backing the image.
 * @param size - The size of the variation, in bytes.
 * @param isDefault - Whether this is the variation served by default.
 * @returns {DatabaseFileImageVariation} The built variation.
 */
function createImageVariation(
  variation: string,
  asset: MockAsset,
  size: number,
  isDefault: boolean,
): DatabaseFileImageVariation {
  const result: DatabaseFileImageVariation = new DatabaseFileImageVariation();

  result.name = variation;
  result.contentType = asset.contentType;
  result.isDefault = isDefault;
  result.size = size;
  result.url = toAssetUrl(asset.path);

  return result;
}
